import { format, parseISO, differenceInCalendarDays } from 'date-fns';

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  try {
    const d = date.includes('T') ? parseISO(date) : new Date(date + 'T00:00:00');
    return format(d, 'MMM d, yyyy');
  } catch {
    return date;
  }
}

export function formatDateTime(date: string | null | undefined): string {
  if (!date) return '—';
  try {
    const d = parseISO(date);
    // Convert to PKT (UTC+5)
    const pkt = new Date(d.getTime() + 5 * 60 * 60 * 1000);
    return format(pkt, 'MMM d, yyyy HH:mm');
  } catch {
    return date;
  }
}

// Days a task is past its due date. Returns null if not overdue, done, or no due date set.
export function getOverdueDays(dueDate: string | null | undefined, status?: string): number | null {
  if (!dueDate || status === 'Done') return null;
  const due = dueDate.includes('T') ? parseISO(dueDate) : new Date(dueDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = differenceInCalendarDays(today, due);
  return days > 0 ? days : null;
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'High': return 'bg-red-100 text-red-700 border-red-200';
    case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'Low': return 'bg-green-100 text-green-700 border-green-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'Done': return 'bg-green-100 text-green-700 border-green-200';
    case 'In Progress': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Pending': return 'bg-gray-100 text-gray-600 border-gray-200';
    case 'Blocked': return 'bg-red-100 text-red-700 border-red-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

export function getStatusDot(status: string): string {
  switch (status) {
    case 'Done': return 'bg-green-500';
    case 'In Progress': return 'bg-blue-500';
    case 'Pending': return 'bg-gray-400';
    case 'Blocked': return 'bg-red-500';
    default: return 'bg-gray-400';
  }
}

export function getProgressPercent(done: number, total: number): number {
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function normalizeUrlForWebsite(url?: string | null, websiteUrl?: string | null): string {
  if (!url) return '';
  if (/^https?:\/\//.test(url)) return url;
  const base = (websiteUrl || 'https://tkxel.com').replace(/\/+$/, '');
  if (url.startsWith('/')) return `${base}${url}`;
  return `https://${url}`;
}

export function parseAssigneeList(primary?: unknown, fallback?: unknown): string[] {
  const names: string[] = [];

  const add = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (!names.some(name => name.toLowerCase() === trimmed.toLowerCase())) {
      names.push(trimmed);
    }
  };

  for (const value of [primary, fallback]) {
    if (Array.isArray(value)) {
      value.forEach(add);
      continue;
    }

    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          parsed.forEach(add);
          continue;
        }
      } catch {
        // Fall back to comma parsing.
      }
    }

    trimmed.split(',').forEach(add);
  }

  return names;
}

export function getTaskAssignees(task: any): string[] {
  return parseAssigneeList(task?.assigned_to_list, task?.assigned_to);
}

export function formatAssigneeNames(task: any): string {
  return getTaskAssignees(task).join(', ');
}

export function getTeamsForAssignees(assignees: string[], members: any[], fallback = ''): string {
  const teams = assignees
    .map(name => members.find(member => member.name?.toLowerCase() === name.toLowerCase())?.team)
    .filter((team): team is string => typeof team === 'string' && team.trim().length > 0);
  const uniqueTeams = Array.from(new Set(teams));
  return uniqueTeams.length > 0 ? uniqueTeams.join(', ') : fallback;
}

export function memberCanSeeTask(task: any, memberName?: string, username?: string): boolean {
  const member = (memberName || '').trim().toLowerCase();
  const user = (username || '').trim().toLowerCase();
  if (!member && !user) return false;

  const assignees = getTaskAssignees(task).map(name => name.toLowerCase());
  const followUpBy = String(task?.follow_up_by || '').trim().toLowerCase();
  const createdBy = String(task?.created_by || '').trim().toLowerCase();

  return (
    (!!member && assignees.includes(member)) ||
    (!!member && followUpBy === member) ||
    (!!user && createdBy === user)
  );
}

export const PRIORITIES = ['Low', 'Medium', 'High'] as const;
export const STATUSES = ['Pending', 'In Progress', 'Done', 'Blocked'] as const;
export const TEAMS = ['SEO', 'Dev', 'Content'] as const;

export type Priority = typeof PRIORITIES[number];
export type Status = typeof STATUSES[number];
export type Team = typeof TEAMS[number];
